import { useEffect, useMemo, useState } from "react";
import { api, formatEUR } from "@/lib/diningApi";
import { useAuth } from "@/context/diningAuth";
import { useWaves } from "@/context/DiningWaveContext";
import { Plus, Trash2, Edit, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const empty = { category: "", concept: "", unit: "", quantity: 1, unit_price: 0, iva_percent: 0, notes: "", order: 0 };

export default function Presupuesto() {
  const { user } = useAuth();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const { currentWave } = useWaves();

  const [data, setData] = useState({ lines: [], grouped: [], total: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [seedConfirm, setSeedConfirm] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => api.get("/budget").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.concept) return toast.error("El concepto es obligatorio");
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 0,
      unit_price: Number(form.unit_price) || 0,
      iva_percent: Number(form.iva_percent) || 0,
      order: Number(form.order) || 0,
    };
    try {
      if (editingId) await api.put(`/budget/${editingId}`, payload);
      else await api.post("/budget", payload);
      toast.success("Línea guardada");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = (line) => {
    setForm({
      category: line.category || "",
      concept: line.concept || "",
      unit: line.unit || "",
      quantity: line.quantity || 0,
      unit_price: line.unit_price || 0,
      iva_percent: line.iva_percent || 0,
      notes: line.notes || "",
      order: line.order || 0,
      auto: !!line.auto,
    });
    setEditingId(line.id);
    setOpen(true);
  };

  const remove = async (line) => {
    if (!window.confirm(`¿Eliminar línea "${line.concept}"?`)) return;
    await api.delete(`/budget/${line.id}`);
    toast.success("Eliminada"); load();
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/budget/seed`, null, { params: { wipe: true } });
      toast.success(`Plantilla generada: ${data.created_lines} líneas`);
      setSeedConfirm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando plantilla");
    } finally { setSeeding(false); }
  };

  const total = useMemo(() => data.total || 0, [data]);

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="presupuesto-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Cliente · {currentWave?.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Presupuesto cliente</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Estructura fija del presupuesto: <strong>Acuerdos · Preproducción · Materiales · Personal eventos · Gestión · Reuniones · Menús</strong>. Las cantidades con etiqueta <em>Auto</em> se sincronizan automáticamente desde los eventos y restaurantes; el resto las editas a mano.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button onClick={() => setSeedConfirm(true)} variant="outline" className="rounded-none border-zinc-300" data-testid="budget-seed-button">
              <Sparkles className="h-4 w-4 mr-2" strokeWidth={1.5} /> Generar plantilla
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="budget-add-button" className="rounded-none bg-zinc-900 hover:bg-zinc-800">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nueva línea
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{editingId ? "Editar" : "Nueva"} línea</DialogTitle>
                  <DialogDescription>El total se calcula como cantidad × precio unitario.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                      <Input data-testid="budget-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" placeholder="Restauración, Producción…" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Unidad</Label>
                      <Input data-testid="budget-field-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-none" placeholder="ud, evento, menú…" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Concepto</Label>
                    <Input data-testid="budget-field-concept" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Cantidad {form.auto && <span className="text-blue-700 font-mono text-[10px]">(auto)</span>}</Label>
                      <Input data-testid="budget-field-qty" type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-none" disabled={form.auto} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Precio unit.</Label>
                      <Input data-testid="budget-field-price" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="rounded-none" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">IVA (%)</Label>
                      <Input data-testid="budget-field-iva" type="number" step="1" value={form.iva_percent} onChange={(e) => setForm({ ...form, iva_percent: e.target.value })} className="rounded-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Notas</Label>
                    <Input data-testid="budget-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="budget-save-button" onClick={save} className="rounded-none bg-zinc-900 hover:bg-zinc-800">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="bg-zinc-900 text-white p-6 mb-8 flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-zinc-400">Total presupuesto</p>
          <p className="text-zinc-400 text-xs mt-1">{data.lines?.length || 0} líneas · {data.grouped?.length || 0} categorías</p>
        </div>
        <p className="font-mono font-heading text-3xl" data-testid="budget-grand-total">{formatEUR(total)}</p>
      </div>

      <div className="space-y-4">
        {(data.grouped || []).length === 0 && (
          <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="budget-empty">
            Aún no hay líneas. {isAdmin && "Pulsa \"Generar plantilla\" para crear las cajas estándar del presupuesto, o añade una línea manualmente."}
          </div>
        )}
        {(data.grouped || []).map((g) => {
          const isCollapsed = collapsed[g.category];
          return (
            <div key={g.category} className="bg-white border border-zinc-200" data-testid={`budget-cat-${g.category}`}>
              <button onClick={() => setCollapsed({ ...collapsed, [g.category]: !isCollapsed })} className="w-full px-6 py-4 border-b border-zinc-200 bg-zinc-50 hover:bg-zinc-100 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-500" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />}
                  <div>
                    <p className="label-eyebrow">Categoría</p>
                    <h3 className="font-heading text-xl mt-0.5">{g.category}</h3>
                  </div>
                </div>
                <div className="flex gap-8 text-right">
                  <div>
                    <p className="label-eyebrow">Líneas</p>
                    <p className="font-mono text-base mt-0.5">{g.lines.length}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Subtotal</p>
                    <p className="font-mono font-heading text-lg mt-0.5">{formatEUR(g.subtotal)}</p>
                  </div>
                </div>
              </button>
              {!isCollapsed && (
                <>
                  <div className="grid grid-cols-12 px-6 py-2 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50/50">
                    <div className="col-span-5">Concepto</div>
                    <div className="col-span-1">Cant.</div>
                    <div className="col-span-1">Unidad</div>
                    <div className="col-span-2">Precio unit.</div>
                    <div className="col-span-2 text-right">Total</div>
                    <div className="col-span-1 text-right">Acciones</div>
                  </div>
                  {g.lines.map((l) => (
                    <div key={l.id} className={`grid grid-cols-12 px-6 py-2.5 border-b border-zinc-100 items-center text-sm hover:bg-zinc-50 ${l.auto ? "bg-blue-50/30" : ""}`} data-testid={`budget-line-${l.id}`}>
                      <div className="col-span-5">
                        <p>{l.concept}</p>
                        <div className="flex gap-2 items-center mt-0.5">
                          {l.auto && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-100 text-blue-800 border border-blue-200 font-mono">Auto · sincronizado</span>}
                          {l.notes && <p className="text-xs text-zinc-500">{l.notes}</p>}
                        </div>
                      </div>
                      <div className="col-span-1 font-mono">{l.quantity}</div>
                      <div className="col-span-1 text-xs text-zinc-600">{l.unit || "—"}</div>
                      <div className="col-span-2 font-mono">{formatEUR(l.unit_price)}</div>
                      <div className="col-span-2 font-mono font-medium text-right">{formatEUR(l.total)}</div>
                      <div className="col-span-1 flex justify-end gap-2">
                        {isAdmin && !l.auto && (
                          <>
                            <button onClick={() => startEdit(l)} data-testid={`budget-edit-${l.id}`}><Edit className="h-4 w-4 text-zinc-600" strokeWidth={1.5} /></button>
                            <button onClick={() => remove(l)} data-testid={`budget-delete-${l.id}`}><Trash2 className="h-4 w-4 text-zinc-600 hover:text-red-600" strokeWidth={1.5} /></button>
                          </>
                        )}
                        {isAdmin && l.auto && (
                          <button onClick={() => startEdit(l)} data-testid={`budget-edit-${l.id}`} title="Editar precio unit. (cantidad bloqueada)"><Edit className="h-4 w-4 text-zinc-400 hover:text-zinc-700" strokeWidth={1.5} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={seedConfirm} onOpenChange={setSeedConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Generar plantilla del presupuesto</DialogTitle>
            <DialogDescription>
              Esto creará 28 líneas predefinidas en la wave actual (<strong>{currentWave?.name}</strong>) agrupadas en 8 categorías: Acuerdos, Preproducción, Materiales, Personal eventos, Gestión, Reuniones y Menús. Las cantidades de <strong>Personal eventos</strong> y <strong>Menús</strong> se sincronizan automáticamente con los eventos confirmados y los restaurantes.<br /><br />
              <span className="text-red-700">Se borrarán todas las líneas existentes en esta wave antes de generar la plantilla.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setSeedConfirm(false)} variant="outline" className="rounded-none" data-testid="seed-cancel">Cancelar</Button>
            <Button onClick={seed} disabled={seeding} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="seed-confirm">
              {seeding ? "Generando…" : "Generar plantilla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
